import React, { useState, useEffect } from 'react';
import { Calculator, Scale, Droplet, Cookie, Square, Thermometer } from 'lucide-react';

type StarterTexture = 'liquid' | 'doughy' | 'stiff';
type FeedingRatio = '1-1-1' | '1-3-3' | '1-5-5' | '1-1-0.7' | '1-2-1.4' | '1-5-3.5' | '1-1-0.5' | '1-2-1' | '1-6-3';

interface FeedingStep {
  starter: number;
  flour: number;
  water: number;
  total: number;
}

const TEXTURE_RATIOS: Record<StarterTexture, FeedingRatio[]> = {
  liquid: ['1-1-1', '1-3-3', '1-5-5'],
  doughy: ['1-1-0.7', '1-2-1.4', '1-5-3.5'],
  stiff: ['1-1-0.5', '1-2-1', '1-6-3']
};

const parseRatio = (ratio: FeedingRatio): { flour: number; water: number } => {
  const [, flour, water] = ratio.split('-').map(Number);
  return { flour, water };
};

export default function StarterCalculator() {
  const [texture, setTexture] = useState<StarterTexture>('liquid');
  const [ratio, setRatio] = useState<FeedingRatio>('1-1-1');
  const [feeds, setFeeds] = useState<string>('1');
  const [finalQuantity, setFinalQuantity] = useState<string>('100');
  const [feedingSteps, setFeedingSteps] = useState<FeedingStep[]>([]);

  useEffect(() => {
    setRatio(TEXTURE_RATIOS[texture][0]);
  }, [texture]);

  useEffect(() => {
    const feedsNum = parseInt(feeds) || 0;
    const quantityNum = parseFloat(finalQuantity) || 0;

    if (quantityNum <= 0 || feedsNum < 1) {
      setFeedingSteps([]);
      return;
    }

    const calculatedSteps: FeedingStep[] = [];
    const { flour, water } = parseRatio(ratio);
    
    const lastFeedStarterQuantity = quantityNum / (1 + flour + water);
    let currentStarterQuantity = lastFeedStarterQuantity;
    
    for (let i = feedsNum - 1; i >= 0; i--) {
      const step: FeedingStep = {
        starter: Math.round(currentStarterQuantity),
        flour: Math.round(currentStarterQuantity * flour),
        water: Math.round(currentStarterQuantity * water),
        total: Math.round(currentStarterQuantity * (1 + flour + water))
      };
      
      calculatedSteps.unshift(step);
      
      if (i > 0) {
        currentStarterQuantity = currentStarterQuantity / (1 + flour + water);
      }
    }

    setFeedingSteps(calculatedSteps);
  }, [texture, ratio, feeds, finalQuantity]);

  const handleFeedsChange = (value: string) => {
    // Allow empty string for better UX during typing
    if (value === '') {
      setFeeds('');
      return;
    }
    
    // Parse the input value
    const numValue = parseInt(value);
    
    // Only update if it's a valid number and >= 1
    if (!isNaN(numValue) && numValue >= 1) {
      setFeeds(value);
    }
  };

  const handleQuantityChange = (value: string) => {
    // Allow empty string for better UX during typing
    if (value === '') {
      setFinalQuantity('');
      return;
    }
    
    // Parse the input value
    const numValue = parseFloat(value);
    
    // Only update if it's a valid number
    if (!isNaN(numValue)) {
      setFinalQuantity(value);
    }
  };

  return (
    <div className="space-y-6 text-white/90">
      <div className="bg-gray-600/60 p-6">
        <div className="space-y-6">
          <div className="flex items-start gap-4 p-4 bg-amber-500/10 backdrop-blur-sm rounded-xl border border-amber-500/20">
            <div className="flex-shrink-0 mt-1">
              <Calculator className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-amber-400 mb-2">About this Calculator</h3>
              <p className="text-sm text-white/90">
                Use this calculator to determine how much starter you need to feed, depending on its texture, the number of feeds and the final quantity required. Whether you prefer a liquid, doughy, or stiff starter, the tool helps you calculate precise ingredient amounts for each feed so that you have the exact quantity of starter and minimize discards.
              </p>
            </div>
          </div>
          
          {/* Texture Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-white/90">
              1. Select a Starter Texture
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { id: 'liquid', label: 'Liquid', icon: Droplet },
                { id: 'doughy', label: 'Doughy', icon: Cookie },
                { id: 'stiff', label: 'Stiff', icon: Square }
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTexture(t.id as StarterTexture)}
                  className={`p-4 border rounded-xl flex items-center gap-3 transition-colors ${
                    texture === t.id
                      ? 'border-amber-400/50 bg-gradient-to-b from-amber-400/20 to-amber-500/10'
                      : 'border-white/20 bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <t.icon className="w-5 h-5 text-amber-400" />
                  <span className="text-white">{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Feeding Ratio Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-white/90">
              2. Select a Feeding Ratio (Starter-Flour-Water)
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {TEXTURE_RATIOS[texture].map((r) => (
                <button
                  key={r}
                  onClick={() => setRatio(r)}
                  className={`p-4 border rounded-xl flex items-center gap-3 transition-colors ${
                    ratio === r
                      ? 'border-amber-400/50 bg-gradient-to-b from-amber-400/20 to-amber-500/10'
                      : 'border-white/20 bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <Scale className="w-5 h-5 text-amber-400" />
                  <span className="text-white">{r.replace(/-/g, ':')}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Number of Feeds */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-white/90">
              3. Set the Number of Feeds
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={feeds}
              onChange={(e) => handleFeedsChange(e.target.value)}
              placeholder="Enter number of feeds"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
            />
          </div>

          {/* Final Quantity Needed */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-white/90">
              4. Set the Final Total Quantity Needed (g)
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={finalQuantity}
              onChange={(e) => handleQuantityChange(e.target.value)}
              placeholder="Enter final quantity needed"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:border-amber-300/50 focus:ring-2 focus:ring-amber-300/20"
            />
          </div>

          {/* Results */}
          {feedingSteps.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-sm text-white font-medium">Your Feeding Schedule</h4>
              <div className="space-y-4">
                {feedingSteps.map((step, index) => (
                  <div key={index} className="p-4 bg-white/10 rounded-xl">
                    <h5 className="text-amber-400 font-medium mb-3">Feed #{index + 1}</h5>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-white/70">Starter</p>
                        <p className="text-lg text-white">{step.starter}g</p>
                      </div>
                      <div>
                        <p className="text-sm text-white/70">Flour</p>
                        <p className="text-lg text-white">{step.flour}g</p>
                      </div>
                      <div>
                        <p className="text-sm text-white/70">Water</p>
                        <p className="text-lg text-white">{step.water}g</p>
                      </div>
                      <div>
                        <p className="text-sm text-white/70">Total in Jar</p>
                        <p className="text-lg text-amber-400">{step.total}g</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-amber-500/10 backdrop-blur-sm rounded-xl mt-6">
                <h4 className="text-amber-400 font-medium mb-2">Final Quantities</h4>
                <p className="text-sm text-white/80">
                  The last feed will give you {feedingSteps[feedingSteps.length - 1].total}g of starter.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { StarterCalculator };